// button.component.ts
import { Component, Input } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  BUTTON_TOKENS,
  BUTTON_FEATURES,
  FIXED_DIMENSIONS,
  FLEXIBLE_PADDING,
  FONT_SIZES,
  ButtonSizeType,
  FixedButtonSize,
  FlexibleButtonSize,
  CIRCULAR_BUTTON_DIMENSIONS,
} from "./button.models";

export type CustomButtonVariant =
  | "default"
  | "modal"
  | "no-hover"
  | "other"
  | "circular";
export type CustomColor =
  | "starlight"
  | "sunbeam"
  | "lupine"
  | "wild-hare"
  | "forest-path"
  | "yellow"
  | "primary"
  | "secondary"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5";
export type FontSize = "xs" | "sm" | "md" | "lg" | "xl" | "xxl" | "2xl" | "3xl";

@Component({
  selector: "app-button",
  templateUrl: "./button.component.html",
  styleUrls: ["./button.component.scss"],
  standalone: true,
  imports: [CommonModule],
})
export class ButtonComponent {
  @Input() variant: CustomButtonVariant = "default";
  @Input() color: CustomColor = "starlight";

  // Size configuration
  @Input() sizeType: ButtonSizeType = "fixed"; // 'fixed' or 'flexible'
  @Input() fixedSize: FixedButtonSize = "md"; // For fixed sizes
  @Input() flexibleSize: FlexibleButtonSize = "md"; // For flexible sizes
  @Input() fontSize: FontSize = "md"; // For font sizes

  @Input() disabled = false;
  @Input() type = "button";

  @Input() fontFamily: string = "var(--font-primary)";

  @Input() isActive: boolean = false;

  get buttonClasses(): string {
    const features = BUTTON_FEATURES[this.variant];
    const classes = ["btn-base", `btn-${this.variant}`];

    // Add circular specific class
    if (this.variant === "circular") {
      classes.push("btn-circular");
    } else {
      // Add size type class
      classes.push(`btn-${this.sizeType}`);

      // Add specific size class
      if (this.sizeType === "fixed") {
        classes.push(`btn-fixed-${this.fixedSize}`);
      } else {
        classes.push(`btn-flexible-${this.flexibleSize}`);
      }
    }

    if (features.hasShadow) classes.push("btn-shadow");
    if (features.hasHover) classes.push("btn-hover");
    if (features.hasActiveState) classes.push("btn-active-state");

    return classes.join(" ");
  }

  get buttonStyles(): Record<string, string> {
    const colorTokens = BUTTON_TOKENS[this.variant];
    const styles = colorTokens?.[this.color as string] || {};

    // Add size-specific styles
    if (this.sizeType === "fixed") {
      const dimensions = FIXED_DIMENSIONS[this.fixedSize];
      return {
        ...styles,
        "--btn-width": dimensions.width,
        "--btn-height": dimensions.height,
        "--btn-font-size": FONT_SIZES[this.fontSize],
      };
    } else if (this.sizeType === "flexible") {
      const padding = FLEXIBLE_PADDING[this.flexibleSize];
      return {
        ...styles,
        "--btn-padding": padding.padding,
        "--btn-font-size": FONT_SIZES[this.fontSize],
      };
    } else if (this.variant === "circular") {
      const dimensions = CIRCULAR_BUTTON_DIMENSIONS;
      return {
        ...styles,
        "--btn-width": dimensions.width,
        "--btn-height": dimensions.height,
      };
    }
  }

  onClick(event: Event) {
    if (!this.disabled) {
      // Handle click logic
    }
  }
}
